import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight, BookOpen, FileText, Calculator, HelpCircle } from "lucide-react";
import { ScrollReveal, StaggerContainer, StaggerItem } from "@/components/ScrollReveal";
import { blogPosts, resourceGuides } from "@/data/blog";
import SEOHead from "@/components/SEOHead";

export default function ResourcesPage() {
  const featuredPosts = blogPosts.filter((p) => p.featured).slice(0, 3);

  return (
    <main data-testid="resources-page">
      <SEOHead
        title="Resources | Engineering Knowledge — Venury"
        description="Technical deep-dives, industry guides, interactive tools, and engineering insights from our practice."
        url="https://venury.tech/resources"
      />
      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-[#0B1116]" />
        <div className="hero-orb-teal" style={{ top: "-10%", right: "-10%" }} />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#0B1116]" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-20 w-full">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-8">
            <span className="tag-box font-mono text-sm text-teal-bright uppercase tracking-[0.2em]">
              Resources
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-[1.05] tracking-tight"
          >
            Engineering <span className="gradient-text-teal">Knowledge</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="text-lg md:text-xl text-slate-400 leading-relaxed max-w-2xl mt-8"
          >
            Technical deep-dives, industry guides, and interactive tools from our engineering practice. 
            Real insights from building production systems.
          </motion.p>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-16 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" staggerDelay={0.08}>
            {[
              { icon: BookOpen, title: "Blog", desc: "Technical articles", path: "/blog" },
              { icon: FileText, title: "Guides", desc: "Industry resources", path: "/resources" },
              { icon: Calculator, title: "ROI Calculator", desc: "Estimate your savings", path: "/resources/roi-calculator" },
              { icon: HelpCircle, title: "Tech Stack Quiz", desc: "Find your ideal stack", path: "/resources/tech-quiz" },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <StaggerItem key={i}>
                  <Link
                    to={item.path}
                    data-testid={`resource-link-${i}`}
                    className="flex items-center gap-4 p-5 rounded-lg border border-white/5 hover:border-teal-primary/30 transition-all duration-300 group"
                  >
                    <div className="w-12 h-12 rounded-lg bg-teal-primary/10 flex items-center justify-center group-hover:bg-teal-primary/20 transition-colors duration-300">
                      <Icon size={22} className="text-teal-bright" />
                    </div>
                    <div>
                      <h3 className="font-heading text-sm font-semibold text-white group-hover:text-teal-bright transition-colors duration-200">{item.title}</h3>
                      <p className="text-xs text-slate-600">{item.desc}</p>
                    </div>
                  </Link>
                </StaggerItem>
              );
            })}
          </StaggerContainer>
        </div>
      </section>

      {/* Featured Blog Posts */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <div className="flex items-end justify-between mb-12">
              <div>
                <span className="font-mono text-sm text-teal-bright uppercase tracking-widest block mb-4">Blog</span>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-white tracking-tight">
                  Latest from Engineering
                </h2>
              </div>
              <Link to="/blog" className="hidden md:inline-flex items-center gap-2 font-mono text-sm text-teal-bright hover:text-white transition-colors duration-200">
                All Posts <ArrowUpRight size={14} />
              </Link>
            </div>
          </ScrollReveal>

          <StaggerContainer className="grid grid-cols-1 md:grid-cols-3 gap-6" staggerDelay={0.1}>
            {featuredPosts.map((post) => (
              <StaggerItem key={post.slug}>
                <Link
                  to={`/blog/${post.slug}`}
                  className="block glass-card rounded-lg overflow-hidden group h-full"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1a252f] to-transparent" />
                    <span className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-wider bg-teal-primary/20 text-teal-bright px-3 py-1 rounded backdrop-blur-sm">
                      {post.category}
                    </span>
                  </div>
                  <div className="p-6">
                    <h3 className="font-heading text-base font-semibold text-white mb-2 group-hover:text-teal-bright transition-colors duration-200 leading-snug">
                      {post.title}
                    </h3>
                    <p className="text-sm text-slate-400 leading-relaxed mb-4 line-clamp-2">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center gap-3 text-xs text-slate-600">
                      <span>{post.readTime}</span>
                      <span className="w-1 h-1 rounded-full bg-slate-600" />
                      <span>{new Date(post.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
                    </div>
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </StaggerContainer>

          <div className="mt-8 md:hidden">
            <Link to="/blog" className="inline-flex items-center gap-2 font-mono text-sm text-teal-bright">
              All Posts <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* Guides & Downloads */}
      <section className="py-20 md:py-32 bg-[#080C10]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <span className="font-mono text-sm text-orange-bright uppercase tracking-widest block mb-4">Guides</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white tracking-tight mb-12">
              In-Depth Resources
            </h2>
          </ScrollReveal>

          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 gap-6" staggerDelay={0.1}>
            {resourceGuides.map((guide, i) => (
              <StaggerItem key={i}>
                <div
                  data-testid={`guide-card-${i}`}
                  className="flex items-start gap-5 p-6 rounded-lg border border-white/5 hover:border-orange-primary/20 transition-colors duration-300"
                >
                  <div className="w-12 h-12 rounded-lg bg-orange-primary/10 flex items-center justify-center flex-shrink-0">
                    <FileText size={22} className="text-orange-bright" />
                  </div>
                  <div>
                    <span className="font-mono text-[10px] text-slate-600 uppercase tracking-wider">{guide.category}</span>
                    <h3 className="font-heading text-base font-semibold text-white mt-1 mb-2">{guide.title}</h3>
                    <p className="text-sm text-slate-400 leading-relaxed mb-4">{guide.shortDesc}</p>
                    <Link
                      to="/contact"
                      className="inline-flex items-center gap-2 font-mono text-xs text-teal-bright hover:text-white transition-colors duration-200 uppercase tracking-wider"
                    >
                      Request Access <ArrowRight size={12} />
                    </Link>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Interactive Tools */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <span className="font-mono text-sm text-teal-bright uppercase tracking-widest block mb-4">Tools</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white tracking-tight mb-12">
              Interactive Tools
            </h2>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <ScrollReveal>
              <Link
                to="/resources/roi-calculator"
                data-testid="roi-calculator-link"
                className="block glass-card rounded-lg p-8 group hover:border-teal-primary/30 transition-colors duration-300"
              >
                <Calculator size={32} className="text-teal-bright mb-6" />
                <h3 className="font-heading text-xl font-bold text-white mb-3 group-hover:text-teal-bright transition-colors duration-200">
                  Project ROI Calculator
                </h3>
                <p className="text-sm text-slate-400 leading-relaxed mb-6">
                  Estimate the return on investment for your next software project. 
                  Factor in development costs, time savings, and revenue impact.
                </p>
                <span className="inline-flex items-center gap-2 font-mono text-xs text-teal-bright uppercase tracking-wider">
                  Calculate ROI <ArrowRight size={12} />
                </span>
              </Link>
            </ScrollReveal>

            <ScrollReveal delay={0.15}>
              <Link
                to="/resources/tech-quiz"
                data-testid="tech-quiz-link"
                className="block glass-card rounded-lg p-8 group hover:border-orange-primary/30 transition-colors duration-300"
              >
                <HelpCircle size={32} className="text-orange-bright mb-6" />
                <h3 className="font-heading text-xl font-bold text-white mb-3 group-hover:text-orange-bright transition-colors duration-200">
                  Tech Stack Recommendation Quiz
                </h3>
                <p className="text-sm text-slate-400 leading-relaxed mb-6">
                  Answer a few questions about your project requirements and get 
                  a tailored technology stack recommendation from our engineering practice.
                </p>
                <span className="inline-flex items-center gap-2 font-mono text-xs text-orange-bright uppercase tracking-wider">
                  Take the Quiz <ArrowRight size={12} />
                </span>
              </Link>
            </ScrollReveal>
          </div>
        </div>
      </section>
    </main>
  );
}
